import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient, {
  fetchComplianceSummary,
  fetchPermissionChanges,
  fetchPrivilegeChanges,
  fetchPublicArtifacts,
} from "../api/client";
import {
  ComplianceSummary,
  PermissionSnapshot,
  PrivilegeChange,
  PublicArtifact,
  Workspace,
} from "../types";
import ExportButton from "../components/ExportButton";
import RegionCoveragePanel from "../components/RegionCoveragePanel";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, formatTime, utcDaysAgo, utcToday } from "../utils/datetime";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** First and last day of the previous calendar quarter (auditors' default). */
// Resolved in UTC throughout, to match the date window it fills. Reading the
// current month and year off the browser's local clock put a user one zone
// either side of a New Year boundary in the wrong quarter entirely — a whole
// evidence pack for the wrong three months, labelled with those months.
function lastQuarterRange(): { from: string; to: string } {
  const now = new Date();
  const q = Math.floor(now.getUTCMonth() / 3); // current quarter 0-3
  const startMonth = (q - 1 + 4) % 4 * 3;
  const year = q === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
  const from = new Date(Date.UTC(year, startMonth, 1));
  // Day 0 of the following month is the last day of this one.
  const to = new Date(Date.UTC(year, startMonth + 3, 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      Loading…
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status chip — reserved status colours, always icon + label (never colour alone)
// ---------------------------------------------------------------------------

// "unknown" is not a severity between OK and Review — it means the data
// behind the control could not be read. It exists because the loads below
// used to swallow their own failures into an empty array, and an empty array
// renders as a green "OK / 0". A control whose evidence is missing must not
// be indistinguishable from a control that passed.
type ControlStatus = "ok" | "review" | "action" | "unknown";

const statusMeta: Record<ControlStatus, { label: string; cls: string; icon: React.ReactNode }> = {
  ok: {
    label: "OK",
    cls: "bg-green-50 text-green-700 border-green-200",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    ),
  },
  review: {
    label: "Review",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  action: {
    label: "Action needed",
    cls: "bg-red-50 text-red-700 border-red-200",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.697 16.126L10.05 3.378c.866-1.5 3.032-1.5 3.898 0l7.353 12.748c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374z" />
      </svg>
    ),
  },
  unknown: {
    label: "Not checked",
    cls: "bg-slate-100 text-slate-700 border-slate-300",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 17.25h.007v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
};

// The API's own words where it has any. A 413 from the access-changes
// endpoint explains that the window is too wide to return without dropping
// rows, and that sentence is the whole reason the control reads as unknown.
function describeError(err: any): string {
  return (
    err?.response?.data?.detail ??
    err?.message ??
    "the request failed"
  );
}

function StatusChip({ status }: { status: ControlStatus }) {
  const m = statusMeta[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${m.cls}`}>
      {m.icon}
      {m.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Control card — one per SOX control area
// ---------------------------------------------------------------------------

function ControlCard({
  title, status, value, unit, caption, onClick,
}: {
  title: string;
  status: ControlStatus;
  value: number | string;
  unit: string;
  caption: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 hover:shadow-md hover:border-blue-300 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <StatusChip status={status} />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-bold text-gray-900 tabular-nums">{value}</span>
        <span className="text-sm text-gray-500">{unit}</span>
      </div>
      <p className="text-xs text-gray-500 leading-snug">{caption}</p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ComplianceDashboardPage() {
  const navigate = useNavigate();
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [accessChanges7d, setAccessChanges7d] = useState<PermissionSnapshot[]>([]);
  const [accessChangesError, setAccessChangesError] = useState<string | null>(null);
  const [monitored, setMonitored] = useState<Workspace[]>([]);
  const [monitoredError, setMonitoredError] = useState<string | null>(null);
  const [recentPrivilege, setRecentPrivilege] = useState<PrivilegeChange[]>([]);
  const [publicArtifacts, setPublicArtifacts] = useState<PublicArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // Default review period: rolling last 3 months up to today, in UTC — the
  // dates are stamped into the evidence-pack filename, so a pack labelled
  // with one window and filled from another misrepresents its own scope to
  // whoever receives it.
  const [packFrom, setPackFrom] = useState(utcDaysAgo(90));
  const [packTo, setPackTo] = useState(utcToday());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // These two are allowed to fail without taking the page down, but the
      // failure is recorded rather than turned into an empty array. The
      // access-changes endpoint answers 413 by design once a window exceeds
      // its row cap — it refuses to truncate audit evidence — and catching
      // that into `[]` reported the refusal as "OK · 0 access changes · 7d",
      // which is the opposite of what the endpoint was telling us.
      const [s, p, a, ac, mw] = await Promise.all([
        fetchComplianceSummary(),
        fetchPrivilegeChanges({ limit: "5" }),
        fetchPublicArtifacts({ active_only: "true" }),
        fetchPermissionChanges({ date_from: utcDaysAgo(7) })
          .then((rows) => ({ rows, failed: null as string | null }))
          .catch((e) => ({ rows: [] as PermissionSnapshot[], failed: describeError(e) })),
        apiClient
          .get<Workspace[]>("/api/workspaces", { params: { monitored_only: "true", include_personal: "true" } })
          .then((r) => ({ rows: r.data, failed: null as string | null }))
          .catch((e) => ({ rows: [] as Workspace[], failed: describeError(e) })),
      ]);
      setSummary(s);
      setRecentPrivilege(p.slice(0, 5));
      setPublicArtifacts(a);
      setAccessChanges7d(ac.rows);
      setAccessChangesError(ac.failed);
      setMonitored(mw.rows);
      setMonitoredError(mw.failed);
      setLastRefreshed(new Date());
    } catch (err) {
      setError("Failed to load compliance data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Derived control states — thresholds mirror the SOX control expectations.
  const accessChangeEvents = accessChanges7d.reduce(
    (n, s) =>
      n + (s.changes?.added?.length ?? 0) + (s.changes?.removed?.length ?? 0) + (s.changes?.roleChanged?.length ?? 0),
    0
  );
  // "We proved the collector cannot read this" and "nobody has checked" are
  // different findings and only the first is a fault. Collapsing them is how
  // a five-day-old note about a self-grant that could not be attempted became
  // a red banner claiming no access to any monitored workspace, while the
  // live probe on System Status reported access to all of them.
  //
  // `no_access` is the only verified denial, because the live probe in
  // access_verifier is the only thing that writes it. `grant_forbidden` used
  // to be counted here too, which contradicted both the API schema and the
  // type in this repo — each says it means "unverified, not no access". It
  // records that a *self-grant* was refused at some past moment; access may
  // have been granted by hand since, which is exactly what happened to the
  // thirteen workspaces this split was introduced for.
  const accessVerifiedOk = (s?: string) => !!s && ["ok", "granted"].includes(s);
  const accessVerifiedDenied = (s?: string) => s === "no_access";
  const wsAccessDenied = monitored.filter((w) => accessVerifiedDenied(w.spAccessStatus));
  // A workspace with no status at all belongs here, not nowhere. Dropping the
  // truthiness check: a newly monitored workspace the verifier has not reached
  // yet was in neither bucket, so it counted as neither verified nor
  // unverified and the header quietly implied assurance nobody had.
  const wsAccessUnverified = monitored.filter(
    (w) => !accessVerifiedOk(w.spAccessStatus) && !accessVerifiedDenied(w.spAccessStatus)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SOX Compliance Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Control status for{" "}
            <button onClick={() => navigate("/workspaces")} className="font-medium text-blue-700 hover:underline">
              {monitoredError
                ? "an unknown number of monitored workspaces"
                : `${monitored.length} monitored workspace${monitored.length !== 1 ? "s" : ""}`}
            </button>
            {wsAccessDenied.length > 0 && (
              <span className="text-red-600 font-medium">
                {" "}· {wsAccessDenied.length} the collector cannot read
              </span>
            )}
            {wsAccessUnverified.length > 0 && (
              <span className="text-amber-600 font-medium">
                {" "}· {wsAccessUnverified.length} with access not yet verified
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-gray-400">
              Last refreshed: {formatTime(lastRefreshed, dateFormat, true)}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 disabled:opacity-50 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading && <Spinner />}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && summary?.countsTruncated && (
        // Every number on the cards below is a floor when this is set, and
        // for "refresh failures" or "open security alerts" a floor makes the
        // control look better than it is. Stated above the cards, because the
        // caveat is worthless underneath the numbers it applies to.
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          At least one count below hit its scan limit or could not be read, so the
          figures are minimums rather than exact totals. Use the linked pages for
          the authoritative view.
        </div>
      )}

      {!loading && !error && summary && (
        <>
          {/* Control status board — one card per SOX control area */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <ControlCard
              title="User Access (SoD)"
              status={accessChangesError ? "unknown" : accessChangeEvents > 0 ? "review" : "ok"}
              value={accessChangesError ? "—" : accessChangeEvents}
              unit="access changes · 7d"
              caption={
                accessChangesError
                  ? `Access changes could not be read: ${accessChangesError}`
                  : "Users added, removed, or role-changed on monitored reports, datasets, and workspaces."
              }
              onClick={() => navigate("/access-changes")}
            />
            <ControlCard
              title="Privileged Roles"
              status={summary.privilegeChangesLast7d > 5 ? "action" : summary.privilegeChangesLast7d > 0 ? "review" : "ok"}
              value={summary.privilegeChangesLast7d}
              unit="role changes · 7d"
              caption="Entra ID directory role assignments and privileged group changes."
              onClick={() => navigate("/privilege-changes")}
            />
            <ControlCard
              title="Change Management"
              status={summary.ualEventsLast24h === 0 ? "review" : "ok"}
              value={summary.deploymentsLast7d}
              unit="deployments · 7d"
              caption={
                summary.ualEventsLast24h === 0
                  ? "No audit events in 24 h — verify collection is healthy."
                  : `${summary.ualEventsLast24h} audit events in the last 24 h.`
              }
              onClick={() => navigate("/deployments")}
            />
            <ControlCard
              title="Data Integrity"
              status={summary.refreshFailuresLast7d > 3 ? "action" : summary.refreshFailuresLast7d > 0 ? "review" : "ok"}
              value={summary.refreshFailuresLast7d}
              unit="refresh failures · 7d"
              caption="Failed dataset refreshes in monitored workspaces — financial data currency."
              onClick={() => navigate("/refresh-health")}
            />
            <ControlCard
              title="Public Exposure"
              status={summary.publicArtifactsActive > 0 ? "action" : "ok"}
              value={summary.publicArtifactsActive}
              unit="exposed reports"
              caption="Reports published to the public web or shared organisation-wide."
              onClick={() => navigate("/risk")}
            />
          </div>

          {/* Needs attention — exceptions first; all clear = one green line */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Needs attention</h2>
            {(() => {
              const items: { severity: ControlStatus; text: string; to: string }[] = [];
              // Listed first, and above the findings, because an unread
              // source changes how much the rest of this panel is worth.
              if (accessChangesError)
                items.push({
                  severity: "unknown",
                  text: `Access changes for the last 7 days could not be read, so this page cannot say whether any need review: ${accessChangesError}`,
                  to: "/access-changes",
                });
              if (monitoredError)
                items.push({
                  severity: "unknown",
                  text: `The monitored workspace list could not be read, so collector access could not be checked: ${monitoredError}`,
                  to: "/workspaces",
                });
              if (summary.publicArtifactsActive > 0)
                items.push({ severity: "action", text: `${summary.publicArtifactsActive} report(s) publicly exposed`, to: "/risk" });
              if (wsAccessDenied.length > 0)
                items.push({
                  severity: "action",
                  text: `Collector cannot read ${wsAccessDenied.length} monitored workspace(s): ${wsAccessDenied.map((w) => w.name).slice(0, 3).join(", ")}${wsAccessDenied.length > 3 ? " …" : ""}`,
                  to: "/workspaces",
                });
              if (wsAccessUnverified.length > 0)
                items.push({
                  severity: "review",
                  text: `Collector access not yet verified on ${wsAccessUnverified.length} monitored workspace(s): ${wsAccessUnverified.map((w) => w.name).slice(0, 3).join(", ")}${wsAccessUnverified.length > 3 ? " …" : ""}`,
                  to: "/workspaces",
                });
              if ((summary.securityAlertsOpen ?? 0) > 0)
                items.push({ severity: "action", text: `${summary.securityAlertsOpen} open security alert(s)`, to: "/risk" });
              if (summary.refreshFailuresLast7d > 0)
                items.push({ severity: "review", text: `${summary.refreshFailuresLast7d} dataset refresh failure(s) in the last 7 days`, to: "/refresh-health" });
              if (accessChangeEvents > 0)
                items.push({ severity: "review", text: `${accessChangeEvents} access change(s) awaiting review`, to: "/access-changes" });
              if ((summary.pimActivationsLast7d ?? 0) > 0)
                items.push({ severity: "review", text: `${summary.pimActivationsLast7d} privileged role activation(s) (PIM) in the last 7 days`, to: "/pim-activations" });
              if (summary.ualEventsLast24h === 0)
                items.push({ severity: "review", text: "No audit events collected in the last 24 h — check System Status", to: "/info" });

              if (items.length === 0) {
                return (
                  <div className="flex items-center gap-2 text-green-700 text-sm py-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">No exceptions — all monitored controls are clear.</span>
                  </div>
                );
              }
              return (
                <ul className="divide-y divide-gray-100">
                  {items.map((item, i) => (
                    <li key={i}>
                      <button
                        onClick={() => navigate(item.to)}
                        className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-gray-50 rounded-lg px-2 -mx-2"
                      >
                        <span className="flex items-center gap-2.5 text-sm text-gray-700">
                          <StatusChip status={item.severity} />
                          {item.text}
                        </span>
                        <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>

          {/* Evidence pack */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Auditor evidence pack</h2>
                <p className="text-xs text-gray-500 mt-1 max-w-xl leading-snug">
                  One ZIP with every SOX evidence CSV for the period — access permissions and changes,
                  activity, privilege changes, refresh history and SLA breaches, deployments, public
                  exposure — plus a manifest recording who generated it and row counts per file.
                </p>
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500">From (UTC)</label>
                  <input
                    type="date"
                    value={packFrom}
                    onChange={(e) => setPackFrom(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500">To (UTC)</label>
                  <input
                    type="date"
                    value={packTo}
                    onChange={(e) => setPackTo(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-transparent select-none">presets</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setPackFrom(utcDaysAgo(90)); setPackTo(utcToday()); }}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      Last 3 months
                    </button>
                    <button
                      onClick={() => { const r = lastQuarterRange(); setPackFrom(r.from); setPackTo(r.to); }}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      Last quarter
                    </button>
                  </div>
                </div>
                <ExportButton
                  endpoint="/api/export/sox-evidence-pack"
                  params={{ date_from: packFrom, date_to: packTo }}
                  filename={`sox-evidence-pack_${packFrom}_${packTo}.zip`}
                  label="Download evidence pack"
                />
              </div>
            </div>
          </div>

          {/* Recent detail — two columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-800">Recent Privilege Changes</h2>
                <button onClick={() => navigate("/privilege-changes")} className="text-xs text-blue-600 hover:underline">
                  View all →
                </button>
              </div>
              {recentPrivilege.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No privilege changes in the last 7 days.</p>
              ) : (
                <ul className="divide-y divide-gray-100 text-sm">
                  {recentPrivilege.map((pc, idx) => (
                    <li key={idx} className="py-2.5 flex flex-col gap-0.5">
                      <div className="flex items-start justify-between">
                        <span className="font-medium text-gray-800 truncate">{pc.activityDisplayName}</span>
                        <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">{formatDate(pc.eventTimestamp)}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        By {pc.initiatedByUpn || pc.initiatedByUser || pc.initiatedByApp || "—"} → {pc.targetName}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-800">Publicly Exposed Reports</h2>
                <button onClick={() => navigate("/risk")} className="text-xs text-blue-600 hover:underline">
                  View all →
                </button>
              </div>
              {publicArtifacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-green-600 gap-2">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium">No publicly shared reports detected</span>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 text-sm">
                  {publicArtifacts.slice(0, 5).map((a) => (
                    <li key={a.reportId} className="py-2.5 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800 truncate">{a.reportName}</div>
                        <div className="text-xs text-gray-500 truncate">{a.workspaceName}</div>
                      </div>
                      <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-red-100 text-red-700 border-red-200">
                        {a.shareType === "publishedToWeb" ? "Public Web" : "Org-Wide"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Where the data lives, and how much of each region we can see */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <RegionCoveragePanel />
          </div>
        </>
      )}
    </div>
  );
}
